import { Dialog } from "@base-ui/react/dialog"
import * as Accordion from "@radix-ui/react-accordion"
import * as Checkbox from "@radix-ui/react-checkbox"
import ReportButtonCBM from "@sop/ConditionBasedMaintenancePage/ReportButtonCBM"
import { useSearch } from "@tanstack/react-router"
import { Button, IconButton, ReportGeneratorDipai } from "@ui-lib/src"
import { SeempActionGroup, SeempReport, UserInfo } from "api"
import { ReactNode, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { MdAssignment, MdCheck, MdClose } from "react-icons/md"
import SeempActionCardPublished from "./SeempActionCardPublished"

interface ReportGeneratorProps {
	plantId: number
	reportData: SeempReport
	actionGroups: SeempActionGroup[]
	usersList: UserInfo[]
	showDeviation: boolean
	sortOrder: "alphAsc" | "alphDesc" | "deviationPositive" | "deviationNegative"
	reportsDeviation: { actionId: number; deviation: number }[]
	opModeIdsNameMap: { opModeIs: number[]; opModeLabel: string }[]
	currentCompany: string
}

export default function SeempReportGenerator({
	plantId,
	reportData,
	actionGroups,
	usersList,
	showDeviation,
	sortOrder,
	reportsDeviation,
	opModeIdsNameMap,
	currentCompany,
}: ReportGeneratorProps) {
	const { t } = useTranslation()
	const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
	const [openItems, setOpenItems] = useState<string[]>([])
	const [printExpanded, setPrintExpanded] = useState<boolean | undefined>(undefined)
	const actionIdStrings = useMemo(
		() => reportData.actions.map((action) => action.id.toString()),
		[reportData.actions]
	)

	const reportContents = reportData.actions
		.sort((a, b) => {
			const aModeName = opModeIdsNameMap.find(
				(m) => `${m.opModeIs.sort()}` === `${a.opModeIds.sort()}`
			)?.opModeLabel
			const bModeName = opModeIdsNameMap.find(
				(m) => `${m.opModeIs.sort()}` === `${b.opModeIds.sort()}`
			)?.opModeLabel
			if (sortOrder === "deviationPositive" || sortOrder === "deviationNegative") {
				const deviationA = reportsDeviation.find((e) => e.actionId === a.id)?.deviation ?? 0
				const deviationB = reportsDeviation.find((e) => e.actionId === b.id)?.deviation ?? 0
				if (deviationA > deviationB)
					return -1 * (sortOrder === "deviationNegative" ? -1 : 1)
				if (deviationA < deviationB) return 1 * (sortOrder === "deviationNegative" ? -1 : 1)
				if (aModeName === undefined || bModeName === undefined) {
					return 0
				}
				return `${aModeName}: ${a.actionType}`.localeCompare(
					`${bModeName}: ${b.actionType}`
				)
			}
			if (aModeName === undefined || bModeName === undefined) {
				return 0
			}
			return sortOrder === "alphAsc"
				? `${aModeName}: ${a.actionType}`.localeCompare(`${bModeName}: ${b.actionType}`)
				: `${bModeName}: ${b.actionType}`.localeCompare(`${aModeName}: ${a.actionType}`)
		})
		.map((action) => (
			<ReportGeneratorDipai.ContentWrapper
				key={`actionCard_report_${action.id}_${printExpanded}`}
				idKey={`actionCard_report_${action.id}_${printExpanded}`}
				colSpan={"100%"}
				rowSpan={printExpanded === true ? "25%" : "5%"}
			>
				<SeempActionCardPublished
					lockedActionList={[]}
					plantId={plantId}
					reportId={reportData.id}
					actionData={action}
					usersList={usersList}
					actionGroups={actionGroups}
					showDeviation={showDeviation}
					targetYear={reportData.year}
					printMode={true}
					isExpanded={printExpanded}
				/>
			</ReportGeneratorDipai.ContentWrapper>
		))

	const onOpenChange = (open: boolean) => {
		setIsReportDialogOpen(open)
		if (open === false) {
			setPrintExpanded(false)
			setPrintExpanded(undefined)
			setOpenItems([])
		}
	}
	const paramSelectedVessel = useSearch({ strict: false })?.vessel ?? ""

	return (
		<Dialog.Root
			open={isReportDialogOpen}
			onOpenChange={onOpenChange}
		>
			<Dialog.Trigger
				className={`text-dipai-secondary-800 hover:text-dipai-secondary-901 hover:bg-dipai-secondary-100 inline-flex h-8 items-center space-x-2 rounded-sm bg-transparent px-3 font-semibold transition-colors focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50`}
			>
				<MdAssignment className="h-5 w-5" />
				<span>{t("REPORT")}</span>
			</Dialog.Trigger>
			<Dialog.Portal>
				<Dialog.Backdrop className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-100 bg-black/75 backdrop-blur-[0.0625rem]" />
				<Dialog.Popup className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] shadow-modal fixed top-1/2 left-1/2 z-100 max-h-[85vh] w-[90vw] max-w-112.5 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-gray-200 bg-white p-4">
					<div className="flex flex-col gap-4">
						<div className="flex flex-row items-center justify-between align-middle">
							<Dialog.Title className="text-dipai-text-primary text-body font-semibold">
								{t("PRINT SEEMP REPORT")}
							</Dialog.Title>
							<div className={`h-8`}>
								<IconButton
									variant="text-dark"
									onClick={() => setIsReportDialogOpen(false)}
								>
									<IconButton.Icon>{MdClose}</IconButton.Icon>
								</IconButton>
							</div>
						</div>
						<Dialog.Description className="text-menu text-dipai-text-primary">
							{t("CHOOSE HOW THE ACTIONS LIST WILL BE PRINTED IN THE REPORT")}
						</Dialog.Description>

						<div className="max-h-[50vh] overflow-y-auto">
							<div className="flex flex-row gap-4">
								<DiagramCard isSelected={printExpanded === false}>
									<CollapsedDiagram />
									<CheckboxComponent
										id="print_collapsed"
										label={t("PRINT LIST COLLAPSED")}
										isChecked={
											printExpanded !== undefined ? !printExpanded : false
										}
										onCheckedChange={() => {
											setOpenItems(
												printExpanded === undefined
													? []
													: printExpanded === true
														? []
														: actionIdStrings
											)
											setPrintExpanded((prev) =>
												prev !== undefined ? !prev : false
											)
										}}
									/>
								</DiagramCard>
								<DiagramCard isSelected={printExpanded === true}>
									<ExpandedDiagram />
									<CheckboxComponent
										id="print_expanded"
										label={t("PRINT LIST EXPANDED")}
										isChecked={
											printExpanded !== undefined ? printExpanded : false
										}
										onCheckedChange={() => {
											setOpenItems(
												printExpanded === true ? [] : actionIdStrings
											)
											setPrintExpanded((prev) =>
												prev !== undefined ? !prev : true
											)
										}}
									/>
								</DiagramCard>
							</div>
						</div>
						<div className="flex justify-end gap-3 border-t pt-2">
							<Button
								variant={"text-dark"}
								onClick={() => setIsReportDialogOpen(false)}
							>
								<Button.Label>{t("CANCEL")}</Button.Label>
							</Button>
							{/* accordion root is necessary here because the seemp action components rendered by the report generator are accordion elements */}
							<Accordion.Root
								className="flex flex-col gap-4"
								type="multiple"
								value={openItems}
								onValueChange={setOpenItems}
							>
								<ReportButtonCBM
									isDisabled={printExpanded === undefined}
									reportContents={reportContents}
									reportName={reportData.name}
									listOfVessels={[paramSelectedVessel]}
									dateFrom={new Date(reportData.year.toString())}
									dateTo={
										new Date().getFullYear() === reportData.year
											? new Date()
											: new Date(
													new Date(
														new Date(
															reportData.year.toString()
														).setFullYear(reportData.year + 1, 0, 0)
													).setHours(23, 59, 59, 999)
												)
									}
									onAfterPrint={() => onOpenChange(false)}
									currentCompany={currentCompany ?? ""}
								/>
							</Accordion.Root>
						</div>
					</div>
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	)
}

export const CheckboxComponent = ({
	id,
	label,
	isChecked,
	onCheckedChange,
}: {
	id: string
	label: string
	isChecked: boolean
	onCheckedChange: () => void
}) => {
	return (
		<div className="grid h-5 grid-cols-[1.25rem_auto] items-center align-middle">
			<Checkbox.Root
				id={id}
				checked={isChecked}
				onCheckedChange={onCheckedChange}
				className="hover:border-dipai-secondary-500 data-[state=checked]:border-dipai-secondary-700 data-[state=checked]:bg-dipai-secondary-700 border-dipai-text-muted flex h-5 w-5 items-center justify-center rounded-sm border-2 bg-white"
			>
				<Checkbox.Indicator>
					<MdCheck className="h-4 w-4 text-white" />
				</Checkbox.Indicator>
			</Checkbox.Root>
			<label
				htmlFor={id}
				className="text-dipai-text-primary text-tiny cursor-pointer pl-3 font-medium"
			>
				{label}
			</label>
		</div>
	)
}

const CollapsedDiagram = () => {
	return (
		<div
			className={`flex h-63 w-45 flex-col gap-[calc((15.75rem-1rem-(13*0.75rem))/12)] overflow-y-hidden rounded-sm bg-gray-300/75 p-2 pb-0`}
		>
			{[...new Array(11)].map((_, index) => (
				<div
					key={`expanded_skeleton_${index}`}
					className={`bg-dipai-secondary-500 overflow-hidden rounded-sm`}
				>
					<div
						key={`collapsed_skeleton_${index}`}
						className={`bg-dipai-secondary-700/50 h-3 w-full rounded-sm`}
					/>
				</div>
			))}
		</div>
	)
}

const ExpandedDiagram = () => {
	return (
		<div
			className={`flex h-63 w-45 flex-col gap-[calc((15.75rem-1rem-(4*3.375rem))/3)] overflow-y-hidden rounded-sm bg-gray-300/75 p-2 pb-0`}
		>
			{[...new Array(3)].map((_, index) => (
				<div
					key={`expanded_skeleton_${index}`}
					className={`bg-dipai-secondary-500 h-13.5 w-full overflow-hidden rounded-sm`}
				>
					<div className={`bg-dipai-secondary-700/50 h-3 w-full`} />
				</div>
			))}
		</div>
	)
}

const DiagramCard = ({ isSelected, children }: { isSelected: boolean; children: ReactNode }) => {
	return (
		<label
			className={`${isSelected === true ? "bg-dipai-secondary-50" : "hover:bg-dipai-secondary-50"} flex cursor-pointer flex-col gap-2 rounded-sm border-0 p-2`}
		>
			{children}
		</label>
	)
}
